import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { formatExpiration, formatPaymentMethodLabel, isBankPaymentMethod } from '@pirouette/shared';
import { supabase } from '@/lib/supabase';
import {
  useFamilyPaymentMethods,
  useOpenCustomerPortal,
  useCreateCheckoutSession,
} from '@/data/stripe';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Empty } from '@/components/ui/Empty';
import { MobilePaymentNotice } from '@/components/ui/MobilePaymentNotice';
import { arePaymentsEnabled } from '@/lib/native/payments';
import { toast } from '@/lib/toast';

export const Route = createFileRoute('/parent/billing/payment-methods')({
  component: ParentPaymentMethods,
});

function useMyFamilyId() {
  return useQuery({
    queryKey: ['my-family-id'],
    queryFn: async (): Promise<string | null> => {
      const u = await supabase.auth.getUser();
      return (u.data.user?.app_metadata.family_id as string | undefined) ?? null;
    },
  });
}

function ParentPaymentMethods() {
  const myFamily = useMyFamilyId();
  const familyId = myFamily.data ?? undefined;
  const cards = useFamilyPaymentMethods(familyId);
  const portal = useOpenCustomerPortal();
  const checkout = useCreateCheckoutSession();
  const paymentsEnabled = arePaymentsEnabled();

  // The off-session installment worker charges the family's DEFAULT method, so
  // surface it prominently. payment_methods is ordered is_default-first.
  const defaultMethod = (cards.data ?? []).find((c) => c.is_default) ?? null;

  async function openPortal() {
    try {
      const resp = await portal.mutateAsync({
        return_url: `${window.location.origin}/parent/billing/payment-methods`,
      });
      window.location.href = resp.url;
    } catch (err) {
      toast({
        title: "Couldn't open portal",
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'error',
      });
    }
  }

  // Phase-8: hosted Stripe Checkout in setup mode — vault a card or bank
  // account as the family's default so installments can be charged
  // automatically. Mirrors the auto-pay enroll flow: invoke the Edge Function,
  // then redirect to the returned hosted URL.
  async function manageDefaultMethod() {
    try {
      const success = `${window.location.origin}/parent/billing/payment-methods?setup=success`;
      const cancel = `${window.location.origin}/parent/billing/payment-methods`;
      const resp = await checkout.mutateAsync({
        mode: 'setup',
        success_url: success,
        cancel_url: cancel,
      });
      window.location.href = resp.url;
    } catch (err) {
      toast({
        title: "Couldn't start setup",
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'error',
      });
    }
  }

  return (
    <div className="space-y-10">
      <header>
        <p className="tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-sm)] uppercase">
          Billing
        </p>
        <h1 className="mt-2">Payment methods</h1>
        <p className="mt-2 max-w-prose text-[var(--color-ink-muted)]">
          Save a default card or bank account so tuition and payment-plan installments can be
          charged automatically. We never see or store your full card or account number — only the
          last four digits for display.
        </p>
      </header>

      {/* Default method — what the installment worker charges off-session. */}
      <section aria-labelledby="default-method-heading" className="surface-elevated space-y-4 p-6">
        <h2
          id="default-method-heading"
          className="font-[var(--font-display)] text-[var(--text-xl)]"
        >
          Default payment method
        </h2>
        {cards.isLoading ? (
          <p aria-live="polite">Loading…</p>
        ) : defaultMethod ? (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="font-medium">{formatPaymentMethodLabel(defaultMethod)}</p>
              {!isBankPaymentMethod(defaultMethod) && (
                <p className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">
                  Expires {formatExpiration(defaultMethod.exp_month, defaultMethod.exp_year)}
                </p>
              )}
            </div>
            <Badge tone="success">Default</Badge>
          </div>
        ) : (
          <p className="text-[var(--color-ink-muted)]">
            No default payment method yet. Add one so automatic charges can go through.
          </p>
        )}
        {paymentsEnabled ? (
          <Button onClick={() => void manageDefaultMethod()} disabled={checkout.isPending}>
            {checkout.isPending
              ? 'Starting…'
              : defaultMethod
                ? 'Update payment method'
                : 'Add payment method'}
          </Button>
        ) : (
          <MobilePaymentNotice action="manage your default payment method" />
        )}
      </section>

      <section aria-labelledby="cards-heading">
        <h2 id="cards-heading" className="text-[var(--text-xl)]">
          Saved methods
        </h2>
        {(cards.data?.length ?? 0) === 0 && !cards.isLoading && (
          <Empty
            className="mt-4"
            title="No saved methods"
            description="Add a default payment method above. You can also add a card during checkout."
          />
        )}
        <ul className="mt-4 grid gap-3 sm:grid-cols-2">
          {(cards.data ?? []).map((c) => {
            const isBank = isBankPaymentMethod(c);
            return (
              <li key={c.id} className="surface flex items-center justify-between p-4">
                <div>
                  <p className="font-medium">{formatPaymentMethodLabel(c)}</p>
                  {!isBank && (
                    <p className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">
                      Expires {formatExpiration(c.exp_month, c.exp_year)}
                    </p>
                  )}
                  {isBank && (
                    <p className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">
                      Bank account (ACH)
                    </p>
                  )}
                </div>
                {c.is_default && <Badge tone="success">Default</Badge>}
              </li>
            );
          })}
        </ul>
      </section>

      {paymentsEnabled ? (
        <Button variant="ghost" onClick={() => void openPortal()} disabled={portal.isPending}>
          {portal.isPending ? 'Opening…' : 'Manage in Stripe portal'}
        </Button>
      ) : (
        <MobilePaymentNotice action="manage payment methods" />
      )}
    </div>
  );
}
